// Dual-mode bounded poll for the Electron auth bootstrap. Extracted from
// protect.tsx so that useElectronAuth tests can mock this function via
// `jest.unstable_mockModule('./poll-for-user-info.js', ...)` without needing
// to restub the co-located hook under test.
// See knowledge: esm-jest-mock-patterns (ai.knowledge/global) — partial
// module mocks are awkward; prefer a sibling module the hook imports.
// See knowledge: electron-auth-bootstrap-race (ai.knowledge/platform) — this
// function encodes the Windows push/pull fallback; do not inline it back
// into protect.tsx without preserving the test seam.

import { ElectronAPI } from '#~/electron/index.js';
import { authStore } from './store.js';

/**
 * Polls getUserInfo IPC and subscribes to authStore until userInfo arrives.
 * On Windows, the push-based IPC 'login' event can be silently dropped if the
 * renderer's listener registers after the main process sends it. Polling
 * getUserInfo as a pull-based fallback eliminates this race.
 */
export const pollForUserInfo = (timeoutMs: number, intervalMs: number): Promise<boolean> => {
  return new Promise((resolve) => {
    if (authStore.getState().userInfo) {
      resolve(true);
      return;
    }

    let settled = false;

    const settle = (result: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(deadline);
      clearInterval(poller);
      unsubscribe();
      resolve(result);
    };

    const deadline = setTimeout(() => settle(false), timeoutMs);

    // Pull: periodically re-invoke getUserInfo IPC
    const poller = setInterval(async () => {
      try {
        // getUserInfo returns { data, status? } post-W2. We only care about
        // `data` inside the poll — once the main process resolves auth, the
        // user-info payload will be non-null regardless of the legacy/status
        // shape the server emits.
        const { data: info } = await ElectronAPI.getUserInfo();
        if (info) {
          authStore.setState({ userInfo: info });
          settle(true);
        }
      } catch {
        // Network error or IPC failure — keep polling
      }
    }, intervalMs);

    // Push: catch the IPC login event if it arrives via authStore
    const unsubscribe = authStore.subscribe((state) => {
      if (state.userInfo) settle(true);
    });
  });
};
